import { computed } from 'vue';
import { useStore } from 'vuex';
import { formatTime } from 'shared/helpers/formatTime';
import {
  getAllocationColors,
  getCategoryIcon,
  isDiscoAllocation,
} from 'dashboard/components-next/planning/Scheduler/allocationCategories';
import { getEventDateRange, getLeafAllocations } from './getEventDateRange';

export function useEventAllocations(event) {
  const store = useStore();

  const allocationDateRange = computed(() => getEventDateRange(event.value));

  const sortedAllocations = computed(() => {
    const allocations = event.value?.allocations;
    if (!allocations || allocations.length === 0) return [];
    return [...allocations].sort(
      (a, b) => new Date(a.start_at) - new Date(b.start_at)
    );
  });

  const childAllocations = computed(() =>
    getLeafAllocations(sortedAllocations.value)
  );

  const getParentAllocation = allocation => {
    if (!allocation.parent_allocation_id) return null;
    return (
      sortedAllocations.value.find(
        a => a.id === allocation.parent_allocation_id
      ) || null
    );
  };

  const isAllocationDisco = allocation =>
    isDiscoAllocation(allocation, getParentAllocation(allocation));

  const getAllocationStyle = allocation => {
    if (isAllocationDisco(allocation)) return {};
    const colors = getAllocationColors(
      allocation,
      getParentAllocation(allocation)
    );
    return {
      backgroundColor: colors.color,
      borderColor: colors.darkColor,
    };
  };

  const getAllocationTextColor = allocation => {
    return getAllocationColors(allocation, getParentAllocation(allocation))
      .textColor;
  };

  const getAllocationIcon = allocation => getCategoryIcon(allocation.category);

  const getResourceName = allocation => {
    if (!allocation.resource_id) return 'Unassigned';
    const resource = store.getters['planningResources/getResourceById'](
      allocation.resource_id
    );
    if (!resource) return 'Unknown';

    if (resource.parent_resource_id) {
      const parentResource = store.getters['planningResources/getResourceById'](
        resource.parent_resource_id
      );
      if (parentResource) return `${parentResource.name} / ${resource.name}`;
    }

    return resource.name;
  };

  const formatAllocationTime = dateStr => formatTime(dateStr);

  const formatAllocationDate = dateStr => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const getAllocationDuration = allocation => {
    const start = new Date(allocation.start_at);
    const end = new Date(allocation.end_at);
    const hours = (end - start) / (1000 * 60 * 60);
    return hours.toFixed(1);
  };

  const formatDurationMinutes = mins => {
    if (mins < 0 || Number.isNaN(mins)) return '';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    const remaining = mins % 60;
    if (remaining === 0) return `${hours}h`;
    return `${hours}h ${remaining}m`;
  };

  const formatAllocationDuration = allocation => {
    if (!allocation?.start_at || !allocation?.end_at) return '';
    const start = new Date(allocation.start_at);
    const end = new Date(allocation.end_at);
    const mins = Math.round((end - start) / (1000 * 60));
    return formatDurationMinutes(mins);
  };

  return {
    allocationDateRange,
    sortedAllocations,
    childAllocations,
    isAllocationDisco,
    getAllocationStyle,
    getAllocationTextColor,
    getAllocationIcon,
    getResourceName,
    formatAllocationTime,
    formatAllocationDate,
    getAllocationDuration,
    formatAllocationDuration,
  };
}
